import type { ProviderDetails } from "@hybrid-iptv/types";

/**
 * Who is asking: "now" is a viewer waiting on a page (a film's details page), "background"
 * is Home getting ready (the banner's candidates). The panel gets one request at a time,
 * and a viewer's request is never queued behind the banner's.
 */
export type DetailsPriority = "now" | "background";

export const PROVIDER_DETAILS_STORE_KEY = "slq.providerDetails.v1";

interface Waiting {
  urgent: boolean;
  started: boolean;
  start: () => Promise<void>;
}

interface Kept {
  at: number;
  d: ProviderDetails;
}

export interface DetailsStorage {
  read(): string | null;
  write(value: string): void;
}

export interface DetailsLineOptions {
  /** One request to the panel (the route answers 502 for a request the panel turned away). */
  ask: (sourceId: string, streamId: string) => Promise<ProviderDetails>;
  storage?: DetailsStorage;
  now?: () => number;
  /** How long an answer is trusted between launches: a plot rarely changes. */
  freshMs?: number;
  /** How many answers are kept. */
  keep?: number;
  saveDelayMs?: number;
}

function browserStorage(): DetailsStorage {
  return {
    read: () => {
      try {
        return localStorage.getItem(PROVIDER_DETAILS_STORE_KEY);
      } catch {
        return null;
      }
    },
    write: (value) => {
      try {
        localStorage.setItem(PROVIDER_DETAILS_STORE_KEY, value);
      } catch {
        // Without storage every launch asks again, as before.
      }
    },
  };
}

/**
 * The panel's film details (`get_vod_info`), asked one at a time, a viewer's request ahead
 * of Home's, and kept between launches.
 *
 * Why: Home's banner asked about its eight candidates in line, and a film page opened
 * meanwhile waited behind all of them, with their retries (owner, 2026-10-12: "details
 * coming up slowly or not at all"). Panels limit how many requests one account has open at
 * once, so the line stays single-file; the fix is who goes first, and not asking twice.
 */
export function createDetailsLine(options: DetailsLineOptions) {
  const { ask, storage = browserStorage(), now = Date.now, freshMs = 7 * 24 * 3_600_000, keep = 300, saveDelayMs = 2_000 } = options;

  let held: Map<string, Kept> | null = null;
  const load = (): Map<string, Kept> => {
    if (held) return held;
    held = new Map();
    try {
      const raw = storage.read();
      if (raw) {
        for (const [key, value] of Object.entries(JSON.parse(raw) as Record<string, Kept>)) {
          if (value && typeof value.at === "number" && value.d && typeof value.d === "object") held.set(key, value);
        }
      }
    } catch {
      // A damaged store is a store with nothing in it.
    }
    return held;
  };

  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  const scheduleSave = () => {
    if (saveTimer !== undefined) return;
    saveTimer = setTimeout(() => {
      saveTimer = undefined;
      const newest = [...load()].sort((a, b) => b[1].at - a[1].at).slice(0, keep);
      held = new Map(newest);
      storage.write(JSON.stringify(Object.fromEntries(newest)));
    }, saveDelayMs);
  };

  const urgent: Waiting[] = [];
  const background: Waiting[] = [];
  const pending = new Map<string, { waiting: Waiting; promise: Promise<ProviderDetails> }>();
  let busy = false;

  const pump = () => {
    if (busy) return;
    const next = urgent.shift() ?? background.shift();
    if (!next) return;
    next.started = true;
    busy = true;
    void next.start().finally(() => {
      busy = false;
      pump();
    });
  };

  const keyOf = (sourceId: string, streamId: string) => `${sourceId}:${streamId}`;

  const promoteWaiting = (waiting: Waiting) => {
    if (waiting.started || waiting.urgent) return;
    const at = background.indexOf(waiting);
    if (at >= 0) background.splice(at, 1);
    waiting.urgent = true;
    urgent.push(waiting);
  };

  /** What is already known about a film, without asking. */
  const known = (sourceId: string, streamId: string): ProviderDetails | undefined => {
    const entry = load().get(keyOf(sourceId, streamId));
    return entry && now() - entry.at < freshMs ? entry.d : undefined;
  };

  return {
    kept: known,

    fetch(sourceId: string, streamId: string, priority: DetailsPriority = "now"): Promise<ProviderDetails> {
      const hit = known(sourceId, streamId);
      if (hit) return Promise.resolve(hit);
      const key = keyOf(sourceId, streamId);
      const existing = pending.get(key);
      if (existing) {
        if (priority === "now") {
          promoteWaiting(existing.waiting);
          pump();
        }
        return existing.promise;
      }
      let waiting!: Waiting;
      const promise = new Promise<ProviderDetails>((resolve, reject) => {
        waiting = {
          urgent: priority === "now",
          started: false,
          start: () =>
            Promise.resolve()
              .then(() => ask(sourceId, streamId))
              .then(
                (details) => {
                  load().set(key, { at: now(), d: details });
                  scheduleSave();
                  resolve(details);
                },
                (error) => reject(error),
              ),
        };
      });
      pending.set(key, { waiting, promise });
      const done = () => {
        pending.delete(key);
      };
      promise.then(done, done);
      (waiting.urgent ? urgent : background).push(waiting);
      pump();
      return promise;
    },

    /** A viewer opened this film while Home's request for it was still waiting: it goes first. */
    promote(sourceId: string, streamId: string): void {
      const existing = pending.get(keyOf(sourceId, streamId));
      if (!existing) return;
      promoteWaiting(existing.waiting);
      pump();
    },
  };
}
